import { AxiosError } from 'axios';
import { http } from 'services';
import {
  LearnArtifact,
  learnArtifactSchema,
  learnArtifactsListSchema,
} from './schema';
const extractErrorMessage = async (error: unknown): Promise<string> => {
  if (error instanceof AxiosError) {
    let payload = error.response?.data as any;

    if (payload instanceof Blob) {
      try {
        payload = JSON.parse(await payload.text());
      } catch {
        payload = null;
      }
    }

    if (typeof payload?.message === 'string') {
      return payload.message;
    }

    if (Array.isArray(payload?.errors)) {
      return payload.errors.join(', ');
    }
  }

  return (error as Error)?.message || 'Unexpected request error';
};

const parseArtifact = (data: unknown): LearnArtifact => {
  const validation = learnArtifactSchema.safeParse(data);
  if (!validation.success) {
    throw new Error('Server returned invalid artifact payload');
  }
  return validation.data;
};

/** The two list scopes the API supports: one document, or every document in a notebook. */
export interface ListArtifactsFilter {
  documentId?: string;
  notebookId?: string;
}

const buildListParams = ({ documentId, notebookId }: ListArtifactsFilter): ListArtifactsFilter | undefined => {
  const params: ListArtifactsFilter = {
    ...(notebookId ? { notebookId } : {}),
    ...(documentId ? { documentId } : {}),
  };
  return Object.keys(params).length > 0 ? params : undefined;
};

export const listArtifacts = async (filter: ListArtifactsFilter = {}): Promise<LearnArtifact[]> => {
  try {
    const response = await http.request.get('/api/learn-artifacts', {
      params: buildListParams(filter),
    });

    const validation = learnArtifactsListSchema.safeParse(response.data?.data);
    if (!validation.success) {
      throw new Error('Server returned invalid artifacts list');
    }

    return validation.data;
  } catch (error) {
    throw new Error(await extractErrorMessage(error));
  }
};

export const getArtifactById = async (artifactId: string): Promise<LearnArtifact> => {
  try {
    const response = await http.request.get(`/api/learn-artifacts/${artifactId}`);
    return parseArtifact(response.data?.data);
  } catch (error) {
    throw new Error(await extractErrorMessage(error));
  }
};

export const generateQuizArtifact = async (input: {
  documentId: string;
  questionCount?: number;
  difficulty?: 'easy' | 'medium' | 'hard';
  language?: string;
  prompt?: string;
  fieldKey?: string;
  fieldPrompt?: string;
}): Promise<LearnArtifact> => {
  try {
    const response = await http.request.post('/api/learn-artifacts/quiz/generate', input);
    return parseArtifact(response.data?.data);
  } catch (error) {
    throw new Error(await extractErrorMessage(error));
  }
};

export const generateBankBatch = async (input: {
  documentId: string;
  // Optional: the destination subject is chosen in the review dialog, once the
  // questions exist and can be judged. When supplied it only labels the draft.
  subjectId?: string;
  questionCount?: number;
  questionTypes: string[];
  difficulty?: 'easy' | 'medium' | 'hard';
  language?: string;
  fieldKey?: string;
  fieldPrompt?: string;
}): Promise<LearnArtifact> => {
  try {
    const response = await http.request.post('/api/learn-artifacts/generate-bank-batch', input);
    return parseArtifact(response.data?.data);
  } catch (error) {
    throw new Error(await extractErrorMessage(error));
  }
};

export const updateArtifactPayload = async (
  artifactId: string,
  payload: Record<string, unknown>,
): Promise<LearnArtifact> => {
  try {
    const response = await http.request.patch(`/api/learn-artifacts/${artifactId}`, { payload });
    return parseArtifact(response.data?.data);
  } catch (error) {
    throw new Error(await extractErrorMessage(error));
  }
};

export const saveArtifactToBank = async (
  artifactId: string,
  // `questionIds` selects which draft questions to bank; omit for all of them.
  // Committing is one-shot per artifact — the bank dedupes on sourceArtifactId,
  // so questions left out here cannot be added by a second call.
  input: { subjectId: string; questionIds?: string[] },
): Promise<{ questionsAdded: number; questionsSkipped: number; subjectId: string }> => {
  try {
    const response = await http.request.post(
      `/api/learn-artifacts/${artifactId}/save-to-bank`,
      input,
    );
    return response.data?.data as {
      questionsAdded: number;
      questionsSkipped: number;
      subjectId: string;
    };
  } catch (error) {
    throw new Error(await extractErrorMessage(error));
  }
};

export const deleteArtifact = async (artifactId: string): Promise<void> => {
  try {
    await http.request.delete(`/api/learn-artifacts/${artifactId}`);
  } catch (error) {
    throw new Error(await extractErrorMessage(error));
  }
};

/** Resolves once the job is accepted; poll the returned `bank_batch` artifact for the questions. */
export const generateToBank = async (input: {
  documentId: string;
  subjectId: string;
  questionCount?: number;
  difficulty?: 'easy' | 'medium' | 'hard';
  language?: string;
  prompt?: string;
  fieldKey?: string;
  fieldPrompt?: string;
}): Promise<LearnArtifact> => {
  try {
    const response = await http.request.post('/api/learn-artifacts/generate-to-bank', input);
    return parseArtifact(response.data?.data);
  } catch (error) {
    throw new Error(await extractErrorMessage(error));
  }
};
